<script setup lang="ts">
import TagInput from '@/components/TagInput.vue'
import { Field, Form } from 'vee-validate'
import { useMyFetch } from '@/utils'
import { useAllStores } from '@/store'
import { handleError } from '@/utils/errors'
import RoleSelector from '../RoleSelector.vue'
import * as yup from 'yup'

const { route, alertStore, authStore, router, t } = useAllStores()
const { data, error } = await useMyFetch(
  `organizations/${route.params.organization_id}/members/${route.params.member_id}`
).json()
await handleError(error, router, authStore, alertStore)
const hasEmail = data.value.email

const schema = yup.object({
  email: yup.string().email(t('email_valid')).optional()
})

function onTagsChange(value: string) {
  data.value.tags = value.split(',').filter((tag: string) => tag)
}

async function onSubmit() {
  const { error } = await useMyFetch(
    `organizations/${route.params.organization_id}/members/${route.params.member_id}`
  )
    .post(data.value)
    .json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
  } else {
    router.back()
  }
}
</script>

<i18n lang="yaml">
en:
  role: Role
  edit: Edit
  save: Save
  tags: Tags
  email: Email
  placeholder_email: "test{'@'}example.com"
  email_valid: Email looks invalid
  email_required: Email is required
ru:
  role: Роль
  edit: Редактировать
  save: Сохранить
  tags: Теги
  email: Электронная почта
  placeholder_email: "test{'@'}example.com"
  email_valid: Электронная почта выглядит неправильно
  email_required: Электронная почта обязательна
</i18n>

<template>
  <div class="card">
    <h4 class="card-header">{{ t('edit') }}</h4>
    <div class="card-body">
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }">
        <div v-if="!hasEmail" class="form-group mb-3">
          <label for="email">{{ t('email') }}</label>
          <Field
            v-model="data.email"
            v-focus
            name="email"
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email }"
            v-bind:placeholder="t('placeholder_email')"
            :validateOnBlur="false"
          />
        </div>

        <div class="form-group mb-3" v-if="data.role !== 'owner'">
          <div class="form-group mb-2">
            <label>{{ t('role') }}</label>
          </div>

          <RoleSelector v-model="data.role" />
        </div>

        <div class="form-group mb-3">
          <label for="tags">{{ t('tags') }}</label>
          <TagInput @change="onTagsChange($event.target.value)" :initial-value="data.tags" />
        </div>

        <div class="form-group mb-3">
          <button class="btn btn-primary" :disabled="isSubmitting">
            <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
            {{ t('save') }}
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>
